import globalstore, {
	setUser,
	deleteMessage,
	setMessageAndMoveCursor,
	getMyUserId,
	getMessage,
	updateMessage,
	setMembership,
} from '@/store'
import store, { getBubble, setVideoSession } from './store'
import {
	bindChannelAppEvents,
	bindChannelRestEvents,
	bindChannelClientEvents,
	unbindChannel,
	getBindEpoch,
	PusherPlugin,
	subscribeToPrivateBubble,
	subscribeToPrivateUser,
} from '@/helpers/pusher'
import { logMeetingEvent } from './common'
import app from '@/app'
import { fetchMyMembershipForChat } from '@/api/chat'

const initPusher = () => {
	//connect to pusher and listen for specific events we need...
	app.use(PusherPlugin)
}

export default initPusher

export const subscribeToPusherChannels = (pusher) => {
	//once pusher connects initialize all our events...
	pusher.connection.bind('connected', () => subscribeToChannels(pusher))
	pusher.connection.bind('error', (error) => {
		console.error('pusher connection error', error)
		logMeetingEvent('pusher connection error', error)
	})
}

const subscribeToChannels = (pusher) => {
	const userChannel = subscribeToPrivateUser(pusher)

	userChannel.unbind() //unbind any orphaned events (user channel — not epoch-guarded)

	bindChannelAppEvents(userChannel, {
		BubbleChanged: bubbleChangedHandler,
		BubbleRemoved: bubbleRemovedHandler,
		UserUpdated: userUpdatedHandler,
		MembershipUpdated: membershipUpdatedHandler,
	})

	const { id, channelcode } = getBubble()
	const bubbleChannel = pusher.subscribe(`private-bubble.${id}.${channelcode}`)
	unbindChannel(bubbleChannel) //unbind any orphaned events + bump epoch

	bindToBubbleChannelEvents(bubbleChannel)
}

// Which bubble channel our handlers are currently bound to. Rebinding
// without this guard would stack duplicate handler sets, and a blanket
// unbind here would wipe any other consumer's handlers on a shared channel —
// so guard on object identity + bind epoch, same as ChatPanel.connectPusher.
// A WeakRef keeps identity semantics (a recreated channel with the same name
// is a different object and rebinds — name + epoch false-matches there,
// since both old and new pass unbindChannel and land on equal epochs)
// without retaining the last meeting's channel for the page's lifetime.
let boundBubbleChannelRef = null
let boundBubbleEpoch = -1

// exported for tests — spec ordering must not mask a stale-guard regression
export const resetBubbleBindState = () => {
	boundBubbleChannelRef = null
	boundBubbleEpoch = -1
}

// exported for pusher.spec.js — the guard is where a mistake is invisible
export const bindToBubbleChannelEvents = (bubbleChannel) => {
	if (!bubbleChannel) return
	if (
		boundBubbleChannelRef?.deref() === bubbleChannel &&
		boundBubbleEpoch === getBindEpoch(bubbleChannel)
	) {
		return
	}
	bindChannelAppEvents(bubbleChannel, {
		MessageTransAdded: messageTransAddedHandler,
		VideosessionUpdated: videosessionUpdatedHandler,
		ReactionAdded: reactionAddedHandler,
		ReactionRemoved: reactionRemovedHandler,
	})

	// REST-style message events (flat payload, no App\Events\ prefix)
	bindChannelRestEvents(bubbleChannel, {
		MessageCreated: messageAddedHandler,
		MessageUpdated: messageUpdatedHandler,
		MessageDeleted: messageRemovedHandler,
	})

	bindChannelClientEvents(bubbleChannel, {
		UserTyping: userTypingHandler,
		UserStoppedTyping: userStoppedTypingHandler,
	})
	boundBubbleChannelRef = new WeakRef(bubbleChannel)
	boundBubbleEpoch = getBindEpoch(bubbleChannel)
}

export const triggerTyping = (pusher) => {
	const { id, channelcode } = getBubble()
	const bubbleChannel = pusher.subscribe(`private-bubble.${id}.${channelcode}`)

	bubbleChannel.trigger('client-App\\Events\\UserTyping', {
		user_id: globalstore.auth.user.id,
	})
}

const bubbleChangedHandler = async ({ bubble }) => {
	// Signal to get our latest bubble info and update our local state
	if (bubble.id !== store.bubble.id) return
	try {
		await fetchMyMembershipForChat(bubble.id)
		const channel = await subscribeToPrivateBubble(bubble.id)
		// no unbind here — the identity + epoch guard in
		// bindToBubbleChannelEvents makes this a no-op when our handlers are
		// still attached, and a blanket unbind would strip other consumers'
		// handlers from a shared channel
		bindToBubbleChannelEvents(channel)
	} catch (error) {
		// pusher invokes this handler directly, so a rejection here would
		// otherwise vanish as an unhandled rejection
		console.error('failed to rebind bubble channel', bubble.id, error)
		logMeetingEvent('failed to rebind bubble channel', error)
	}
}

const bubbleRemovedHandler = ({ bubble }) => {
	if (bubble.id === store.bubble.id) {
		//TODO we just got kicked out of our current bubble (someone deleted it) what do we do???
	}
}

const userUpdatedHandler = ({ user }) => {
	// see if the user updated exists in our memberships array and if so update it
	if (store.membersByUserId.hasOwnProperty(user.id)) {
		setUser(user)
	}
}

const membershipUpdatedHandler = ({ membership }) => {
	// If the membership exists in our current state we should update it
	if (membership.bubble_id === store.bubble.id) {
		if (store.membersByUserId.hasOwnProperty(membership.user_id)) {
			setMembership(membership)
		}
	}
}

const messageAddedHandler = (message) => {
	setMessageAndMoveCursor(message)
}

const messageUpdatedHandler = (message) => {
	deleteMessage(message.local_id || message.uuid)
	updateMessage(message.id, (prev) => {
		if (prev && prev.messagetrans) {
			// messagetrans is not included in updates so need to
			// persist it if the old version of the message had one
			message.messagetrans = prev.messagetrans
		}
		return message
	})
}

const messageRemovedHandler = ({ id }) => {
	const message = getMessage(id)
	// If message is in store but belongs to a different chat, ignore.
	// If not in store, allow the delete (no-op for missing messages).
	if (message && message.bubble_id !== getBubble().id) return
	deleteMessage(id)
}

const messageTransAddedHandler = ({ messagetrans }) => {
	updateMessage(messagetrans.message_id, { messagetrans })
}

const userTypingHandler = ({ user_id }) => {
	if (user_id === globalstore.auth.user.id) {
		return
	}

	let i = store.typinglist.map((t) => t.user_id).indexOf(user_id)
	let typing = null

	if (i >= 0) {
		typing = store.typinglist[i]
	} else {
		typing = { user_id: user_id, timeout: null }
		store.typinglist.push(typing)
	}

	if (typing.timeout) {
		window.clearTimeout(store.typinglist[i].timeout)
	}

	typing.timeout = window.setTimeout(() => {
		store.typinglist = store.typinglist.filter((t) => {
			return typing.user_id != t.user_id
		})
	}, 5000)
}

const userStoppedTypingHandler = ({ user_id }) => {
	store.typinglist = store.typinglist.filter((t) => {
		return user_id != t.user_id
	})
}

const videosessionUpdatedHandler = ({ videosession }) => {
	setVideoSession(videosession)
}

function reactionAddedHandler({
	message_id,
	emoji,
	emoji_base,
	user_id,
	count,
}) {
	const me = user_id === getMyUserId()
	const message = getMessage(message_id)
	const currentReactions = { ...message.reactions }
	const existingReaction = currentReactions[emoji]

	if (existingReaction) {
		// Update existing reaction
		currentReactions[emoji] = {
			...existingReaction,
			count,
			me: me || existingReaction.me,
			users: me ? [...existingReaction.users, user_id] : existingReaction.users,
			emoji,
			emoji_base,
		}
	} else {
		// Create new reaction
		currentReactions[emoji] = {
			users: [user_id],
			me,
			count,
			emoji,
			emoji_base,
		}
	}

	updateMessage(message.id, {
		reactions: currentReactions,
	})
}

function reactionRemovedHandler({
	message_id,
	emoji,
	emoji_base,
	user_id,
	count,
}) {
	const me = user_id === getMyUserId()
	const message = getMessage(message_id)
	const currentReactions = { ...message.reactions }

	if (count === 0) {
		// Remove emoji variant if count is 0
		delete currentReactions[emoji]
	} else {
		// Update emoji variant if it exists
		const reaction = currentReactions[emoji]
		if (reaction) {
			currentReactions[emoji] = {
				...reaction,
				count,
				me: me ? false : reaction.me,
				users: me ? [] : [user_id],
				emoji,
				emoji_base,
			}
		}
	}

	updateMessage(message.id, {
		reactions: currentReactions,
	})
}
